import { Alert, AlertColor, Snackbar } from '@mui/material';
import { createContext, ReactNode, useContext, useState } from 'react';
import { logError } from '../Lib/logger';

type ToastContextType = {
  showToast: (message: string, severity?: AlertColor) => void;

  /**
   * Show an error toast with a message based on the error and the attempted action.
   * Automatically logs the error to DataDog for monitoring.
   * @param error - The error to show.
   * @param attemptedAction - The action that was attempted. Should be a verb phrase like "update a market" or "create a market". This should be an infinitive verb phrase (e.g., "update a market"), which acts as a noun phrase describing the action in English grammar, ensuring it fits naturally into an error message.
   * @returns void.
   */
  showErrorToast: (error: any, attemptedAction?: string) => void;

  tryWithErrorToast: <T>(context: string, callback: () => Promise<T>) => Promise<T | undefined>;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('success');

  const showToast = (message: string, severity: AlertColor = 'success') => {
    setMessage(message);
    setSeverity(severity);
    setOpen(true);
  };

  const showErrorToast = (error: any, attemptedAction?: string) => {
    logError(
      attemptedAction ? `Failed to ${attemptedAction}` : 'An error occurred',
      error,
      {
        attemptedAction,
        status: error.status,
        errorMessage: error.message,
      },
    );

    // Kai's own 503s say what is down and to try again; a load balancer's page has no message, so it gets the generic one.
    if (error.status === 503 && typeof error.remoteMessage === 'string' && error.remoteMessage) {
      showToast(error.remoteMessage, 'error');
      return;
    }

    switch (error.status) {
      case 403:
      case 401:
        showToast(`You are not authorized${attemptedAction ? ` to ${attemptedAction}` : ''}.`, 'error');
        break;
      case 404:
        showToast(`The resource you are looking for does not exist${attemptedAction ? ` for ${attemptedAction}` : ''}.`, 'error');
        break;
      default:
        if (error.status >= 500) {
          showToast(`We're sorry. Something went wrong on our end${attemptedAction ? ` while ${attemptedAction}` : ''}. Please try again.`, 'error');
        } else {
          showToast(error.message, 'error');
        }
    }
  };

  const tryWithErrorToast = async <T,>(context: string, callback: () => Promise<T>): Promise<T | undefined> => {
    try {
      return await callback();
    } catch (error) {
      showErrorToast(error, context);
      return undefined;
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ToastContext.Provider value={{ showToast, showErrorToast, tryWithErrorToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        // Above the full-screen modals (z-index 2000) and dialogs over them (3000), so errors raised inside them are seen.
        sx={{ zIndex: 3100 }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};
